import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { HttpClient } from "@angular/common/http";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { ProofChatMessage } from "./proofChatMessage";

/** Payload emitted when the user resubmits a revised proof from the chat panel. */
export interface ProofResubmitEvent {
  /** The message/comment to attach to the resubmission. */
  comment: string;
  /** The revised proof file selected by the user, or `null` to reuse the latest proof file. */
  file: File | null;
}

@Component({
  selector: "jni-pdf-viewer-page",
  templateUrl: "./pdf-viewer-page.component.html",
  styleUrls: ["./pdf-viewer-page.component.scss"],
  imports: [CommonModule, FormsModule, NgxExtendedPdfViewerModule],
})
export class PdfViewerPageComponent implements OnChanges, AfterViewChecked {
  @Input() base64pdf = "";
  @Input() fileNameToDownload = "document";
  @Input() isModal = false;
  @Input() showChat = false;
  /** The proof conversation messages to render, most-recent last. */
  @Input() chatMessages: ProofChatMessage[] = [];
  /** `true` while the chat history is loading. */
  @Input() chatLoading = false;
  /** `true` to show the resubmit composer (Sales only). */
  @Input() canResubmit = false;
  /** `true` while a resubmission request is in flight. */
  @Input() resubmitting = false;
  @Output() closePdfStackerEvent = new EventEmitter();
  /** Emits the comment and revised file when the user clicks Resubmit. */
  @Output() resubmitProof = new EventEmitter<ProofResubmitEvent>();

  /** The scrollable chat message list, used to auto-scroll to the latest message. */
  @ViewChild("chatBody") private readonly chatBody?: ElementRef<HTMLDivElement>;

  @ViewChild("proofFileInput")
  private readonly proofFileInput?: ElementRef<HTMLInputElement>;

  /** The message text typed by the user for the next resubmission. */
  message = "";

  /** Tracks whether an empty resubmission was attempted. */
  messageAttempted = false;

  /** The revised proof file selected for resubmission, or `null`. */
  selectedFile: File | null = null;

  /** `true` while a file is being dragged over the upload icon. */
  isDragOver = false;

  /** Tracks whether the chat thread should be scrolled to the bottom after the next view check. */
  private shouldScrollChatToBottom = false;

  private readonly http = inject(HttpClient);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["chatMessages"]) {
      this.shouldScrollChatToBottom = true;
    }
  }

  ngAfterViewChecked(): void {
    const element = this.chatBody?.nativeElement;
    // Wait until the chat body is attached and laid out (dialog may still be opening).
    if (this.shouldScrollChatToBottom && element?.offsetParent) {
      this.shouldScrollChatToBottom = false;
      element.scrollTop = element.scrollHeight;
    }
  }

  /** Scrolls the chat to the latest message; call when the hosting dialog finishes opening. */
  scrollChatToBottom(): void {
    this.shouldScrollChatToBottom = true;
    const element = this.chatBody?.nativeElement;
    if (element) {
      requestAnimationFrame(() => {
        element.scrollTop = element.scrollHeight;
      });
    }
  }

  closePdfStacker() {
    this.closePdfStackerEvent.emit();
  }

  /** Captures the file chosen via the attachment picker. */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  /** Highlights the upload icon while a file is dragged over it. */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  /** Accepts a dropped file if it matches the allowed proof types. */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    if (!["pdf", "png", "jpg", "jpeg"].includes(ext)) {
      return;
    }
    this.selectedFile = file;
    if (this.proofFileInput) {
      this.proofFileInput.nativeElement.value = "";
    }
  }

  /** Emits the resubmission when a comment is present (a revised file is optional). */
  onResubmit(): void {
    if (this.resubmitting) {
      return;
    }
    if (!this.message.trim()) {
      this.messageAttempted = true;
      return;
    }
    this.messageAttempted = false;
    this.resubmitProof.emit({
      comment: this.message.trim(),
      file: this.selectedFile,
    });
  }

  /** Returns `true` when the loaded proof file is an image (png, jpg, or jpeg). */
  get isImageFile(): boolean {
    const ext =
      (this.fileNameToDownload ?? "").split(".").pop()?.toLowerCase() ?? "";
    return ext === "png" || ext === "jpg" || ext === "jpeg";
  }

  /** Returns the MIME type string for the current image proof, or `'image/jpeg'` as fallback. */
  get imageMimeType(): string {
    const ext =
      (this.fileNameToDownload ?? "").split(".").pop()?.toLowerCase() ?? "";
    return ext === "png" ? "image/png" : "image/jpeg";
  }

  /** Resets the composer when the proof dialog closes or a resubmission succeeds. */
  resetComposer(): void {
    this.message = "";
    this.messageAttempted = false;
    this.selectedFile = null;
    if (this.proofFileInput) {
      this.proofFileInput.nativeElement.value = "";
    }
  }

  /** `trackBy` for the chat message list so unchanged messages are not re-rendered when new ones arrive. */
  trackByMessageId(_index: number, msg: ProofChatMessage): number {
    return msg.id;
  }

  /**
   * Downloads the proof file attached to a chat message via HttpClient so that the
   * MSAL Bearer token is included for API-backed file URLs.
   */
  downloadChatFile(msg: ProofChatMessage): void {
    if (!msg.fileUrl) {
      return;
    }
    this.http.get(msg.fileUrl, { responseType: "blob" }).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = msg.fileName || "proof";
      anchor.click();
      URL.revokeObjectURL(url);
    });
  }
}
